import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { ShieldWarning, Warning, FileText, Prohibit } from "@phosphor-icons/react";

const fmt = (n) => new Intl.NumberFormat("de-DE").format(n || 0);

const statusStyle = {
  paid: { text: "BEZAHLT", cls: "bg-tactical-success/20 text-tactical-success border-tactical-success/40" },
  open: { text: "OFFEN", cls: "bg-tactical-danger/20 text-tactical-danger border-tactical-danger/40" },
  closed: { text: "ABGESCHLOSSEN", cls: "bg-white/10 text-white/60 border-white/20" },
};

export default function Record() {
  const [records, setRecords] = useState([]);
  const [warnings, setWarnings] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/character/record"),
      api.get("/character/warnings"),
    ]).then(([r, w]) => { setRecords(r.data); setWarnings(w.data); })
      .catch(() => toast.error("Fehler beim Laden"));
  }, []);

  const totalFines = records.reduce((a, b) => a + (b.fine || 0), 0);
  const totalJail = records.reduce((a, b) => a + (b.jail_months || 0), 0);

  return (
    <div>
      <PageHeader
        eyebrow="05 // CRIMINAL RECORD"
        title="Polizei-Akte"
        description="Klassifizierte Akte — LSPD Datenbank Auszug"
      />

      <div className="p-4 md:p-8 lg:p-12 space-y-6 md:space-y-8">
        {/* Classified banner */}
        <div className="border border-tactical-danger/60 bg-tactical-danger/5 p-4 flex items-center gap-3 scanline">
          <ShieldWarning size={22} className="text-tactical-danger" weight="fill" />
          <div className="font-mono text-xs tracking-widest text-tactical-danger uppercase">
            // CLASSIFIED — LSPD ARCHIV — NUR FÜR AUTORISIERTE ZWECKE //
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-4">
          <div className="border border-tactical-border bg-tactical-surface p-5">
            <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest">Einträge</div>
            <div className="font-mono text-3xl font-bold mt-2" data-testid="rec-total">{records.length}</div>
          </div>
          <div className="border border-tactical-border bg-tactical-surface p-5">
            <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest">Bußgelder</div>
            <div className="font-mono text-3xl font-bold mt-2 text-tactical-danger">${fmt(totalFines)}</div>
          </div>
          <div className="border border-tactical-border bg-tactical-surface p-5">
            <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest">Haftmonate</div>
            <div className="font-mono text-3xl font-bold mt-2 text-tactical-warn">{totalJail}m</div>
          </div>
        </div>

        {/* Records list */}
        <div>
          <div className="font-mono text-xs text-white/40 uppercase tracking-widest mb-3 flex items-center gap-2">
            <FileText size={14} /> Straftaten
          </div>
          {records.length === 0 ? (
            <div className="border border-tactical-border border-dashed p-12 text-center font-mono text-tactical-success">
              // KEINE EINTRÄGE — SAUBERE AKTE //
            </div>
          ) : (
            <div className="border border-tactical-border bg-tactical-surface divide-y divide-tactical-border">
              <div className="grid grid-cols-12 gap-4 px-4 py-3 font-mono text-[10px] text-white/40 uppercase tracking-widest bg-tactical-elev">
                <div className="col-span-5">Straftat</div>
                <div className="col-span-2 text-right">Bußgeld</div>
                <div className="col-span-1 text-right">Haft</div>
                <div className="col-span-2">Officer</div>
                <div className="col-span-2 text-right">Status</div>
              </div>
              {records.map(r => (
                <div key={r.id} className="grid grid-cols-12 gap-4 px-4 py-3 font-mono text-sm items-center hover:bg-tactical-elev transition-colors">
                  <div className="col-span-5">
                    <div className="text-white">{r.offense}</div>
                    <div className="text-[10px] text-white/40">{new Date(r.date).toLocaleString("de-DE")}</div>
                  </div>
                  <div className="col-span-2 text-right text-tactical-danger">${fmt(r.fine)}</div>
                  <div className="col-span-1 text-right text-tactical-warn">{r.jail_months}m</div>
                  <div className="col-span-2 text-white/70">{r.officer}</div>
                  <div className="col-span-2 text-right">
                    <span className={`inline-block border px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ${statusStyle[r.status]?.cls}`}>
                      {statusStyle[r.status]?.text}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Warnings */}
        <div>
          <div className="font-mono text-xs text-white/40 uppercase tracking-widest mb-3 flex items-center gap-2">
            <Warning size={14} /> Admin Warnungen
          </div>
          {warnings.length === 0 ? (
            <div className="border border-tactical-border border-dashed p-8 text-center font-mono text-white/40 text-sm">
              Keine Admin-Warnungen
            </div>
          ) : (
            <div className="border border-tactical-border bg-tactical-surface divide-y divide-tactical-border">
              {warnings.map(w => (
                <div key={w.id} className="p-4 flex items-start gap-3">
                  <Prohibit size={18} className="text-tactical-warn mt-0.5" />
                  <div className="flex-1">
                    <div className="text-white">{w.reason}</div>
                    <div className="font-mono text-[10px] text-white/40 mt-1">
                      Ausgestellt von {w.admin} · {new Date(w.date).toLocaleString("de-DE")}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
